import { AppstoreOutlined } from "@ant-design/icons";
import { Button, Dropdown, MenuProps, Skeleton, Spin, theme } from "antd";
import { ExploreDeepLink } from "pages/[orgId]/topics/[topicId]/index.page";
import { useDigestLinks } from "pages/[orgId]/topics/useDigestLinks";
import {
  AnnotatedDeepLink,
  DeepLinkType,
  DigestV2State,
  ExploreCard,
} from "pages/api/deep-links/types";
import { useEffect, useState } from "react";
import { Flex } from "src/components/Flex";
import { NavigableDeepLink } from "src/components/NavigableDeepLink";
import { Condition } from "src/search/types";

export function DigestDataLinks(
  orgId: string,
  link: AnnotatedDeepLink<DigestV2State>,
  filters?: Condition[],
  displayTitle: boolean = true
): MenuProps {
  const keys = link.json?.data ? Object.keys(link.json?.data) : [];

  const items = keys.map((key, idx) => {
    const digest = link?.json?.data[key];
    return {
      key: idx,
      label: (
        <ExploreDeepLink
          key={key}
          deepLink={digest as unknown as ExploreCard}
          orgId={orgId}
          spanRow={true}
          filters={filters}
        />
      ),
    };
  });
  return {
    items: [
      {
        key: "default",
        label: displayTitle ? <>Supporting Queries</> : null,
        disabled: true,
      },
      ...items,
    ],
  };
}

export function DigestBoardLinks(props: {
  orgId: string;
  link: AnnotatedDeepLink<DigestV2State>;
  filters?: Condition[];
}) {
  const { links, isLoading } = useDigestLinks(props.link);
  const [items, setItems] = useState<MenuProps>({ items: [] });
  useEffect(() => {
    if (isLoading || !links) return;
    setItems({
      items: links.map((link, idx) => ({
        key: idx,
        label:
          link.type === DeepLinkType.EXPLORE ? (
            <ExploreDeepLink
              key={idx}
              deepLink={link.json as unknown as ExploreCard}
              orgId={props.orgId}
              spanRow={true}
              filters={props.filters}
            />
          ) : (
            <NavigableDeepLink
              key={idx}
              deepLink={link}
              orgId={props.orgId}
              spanRow={true}
              filters={props.filters}
            />
          ),
      })),
    });
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps
  if (isLoading) return <Spin />;
  return (
    <Dropdown menu={items} placement="topRight">
      <Button
        onClick={(e) => e.preventDefault()}
        icon={<AppstoreOutlined />}
        shape="round"
        size="small"
        style={{ border: 0 }}
      >
        Related Boards
      </Button>
    </Dropdown>
  );
}

export const DigestLinks = ({
  title,
  orgId,
  link,
  filters,
  displayTitle = true,
}: {
  title?: string;
  orgId: string;
  link: AnnotatedDeepLink<DigestV2State>;
  filters: Condition[];
  displayTitle?: boolean;
}) => {
  const { links, isLoading } = useDigestLinks(link);
  const { token } = theme.useToken();
  if (isLoading) return <Skeleton active />;
  return (
    <Flex style={{ justifyContent: "space-between" }} vertical>
      <div style={{ width: "100%" }}>
        {displayTitle && (
          <div style={{ color: token.colorTextTertiary }}>{title}</div>
        )}
        <div>
          {links.map((link, idx) => {
            if (link.type === DeepLinkType.EXPLORE) {
              return (
                <ExploreDeepLink
                  key={idx}
                  deepLink={link.json as unknown as ExploreCard}
                  orgId={orgId}
                  spanRow={true}
                  filters={filters}
                />
              );
            }
            return (
              <NavigableDeepLink
                key={idx}
                deepLink={link}
                orgId={orgId}
                spanRow={true}
                filters={filters}
              />
            );
          })}
        </div>
      </div>
    </Flex>
  );
};
